import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { FormattedMessage, useIntl } from 'react-intl'
import { delay } from 'lodash'

import { scrollToAnchor } from 'src/helpers/browser'
import { shouldAutoScroll } from 'src/composables/scrollUtils'
import { disclaimerIdForEmailLink } from 'src/constants/disclaimer'
import history from 'src/history'

import {
  selectCorporateProfile,
  selectCustomerProfile,
  selectOfficeDisclaimer,
} from 'src/store/selectors/customerProfile'
import { selectHomeDigestLoading } from 'src/store/selectors/display'

// Components
import { Button, Text } from 'src/Components'
import CompanyDisclaimer from 'src/Components/shared/CompanyDisclaimer'
import NMLS from 'src/Components/shared/NMLS'
import Phone from 'src/Components/shared/Phone'

// Styles
import styles from './Disclaimer.module.scss'

const propTypes = {
  className: PropTypes.string,
  staticContext: PropTypes.any,
}

const Disclaimer = ({
  className,
  staticContext, // ignore
  ...restProps
}) => {
  const corporateProfile = useSelector(selectCorporateProfile) || {}
  const {
    address,
    showAddress,
    logoLightUri: companyLogo,
    name: companyName,
    phone: companyPhone,
    kind,
    nmls,
  } = corporateProfile
  const companyAddress = showAddress && address ? address : null
  const [displayFullDisclaimer, setDisplayFullDisclaimer] = useState(false)
  const formattedNmls =
    kind === 'mortgage_lending_company' && nmls ? nmls : null

  const customerProfile = useSelector(selectCustomerProfile) || {}
  const customerDisclaimer = customerProfile.disclaimer
  const officeDisclaimer = useSelector(selectOfficeDisclaimer)

  const loading = useSelector(selectHomeDigestLoading)
  const intl = useIntl()

  useEffect(() => {
    if (!loading) {
      if (!shouldAutoScroll(history.location, history)) {
        return
      }

      delay(() => scrollToAnchor(history.location.hash, { padding: 32 }), 100)
    }
  }, [loading])

  return (
    <div
      className={classnames('disclaimer', className)}
      id={disclaimerIdForEmailLink}
      {...restProps}
    >
      {companyLogo && (
        <img className={styles.image} src={companyLogo} alt={companyName} />
      )}

      <div className={styles.contactContainer}>
        <Text
          element='span'
          variant='neutralCool'
          variantCount='700'
          className={styles.contactLine}
          weight='bold'
          size='l'
        >
          {companyName}
        </Text>
        <NMLS
          value={formattedNmls}
          variant='neutralCool'
          variantCount='700'
          className={styles.contactLine}
          size='xs'
        />
        <Text size='m' weight='bold' className={styles.infoLabel}>
          {intl.formatMessage({ id: 'FormField.label.phone' })}
        </Text>
        <Phone value={companyPhone} className={styles.contactLine} />
      </div>

      {companyAddress && (
        <>
          <Text size='m' weight='bold' className={styles.infoLabel}>
            {intl.formatMessage({ id: 'FormField.label.streetAddress' })}
          </Text>
          <Text
            element='p'
            variant='neutralCool'
            variantCount='600'
            className={styles.address}
          >
            {companyAddress}
          </Text>
        </>
      )}
      {!companyAddress && <br />}
      {customerDisclaimer && (
        <div className='disclaimer-body'>
          <Text element='p' variant='neutralCool' variantCount='700'>
            {customerDisclaimer}
          </Text>
        </div>
      )}
      {displayFullDisclaimer && officeDisclaimer && (
        <div className='disclaimer-body'>
          <Text element='p' variant='neutralCool' variantCount='700'>
            {officeDisclaimer}
          </Text>
        </div>
      )}
      <div className='disclaimer-body'>
        <Text element='p' variant='neutralCool' variantCount='700'>
          {!displayFullDisclaimer && (
            <FormattedMessage
              id='Disclaimer.body.firstParagraph'
              defaultMessage='APR is based on a reasonably current index and margin from the Freddie Mac PMMS. This message is for information purposes only and is not an advertisement to extend customer credit as defined by Section 12 CFR 1026.2 Regulation Z. Program rates, terms, and conditions are subject to change at any time. All examples of potential mortgage products above are estimates for illustration purposes only. All programs and mortgage products are subject to borrower income, credit, and property approval. Not all borrowers will qualify. This is not an offer for extension of credit or a commitment to lend. Home valuations are estimates purchased from a third-party data provider. Actual appraised value at the time of a transaction may vary.'
            />
          )}
          {displayFullDisclaimer && (
            <>
              <FormattedMessage
                id='Disclaimer.bodyHTML'
                defaultMessage='<p>APR is based on a reasonably current index and margin from the Freddie Mac PMMS. This message is for information purposes only and is not an advertisement to extend customer credit as defined by Section 12 CFR 1026.2 Regulation Z. Program rates, terms, and conditions are subject to change at any time. All examples of potential mortgage products above are estimates for illustration purposes only. All programs and mortgage products are subject to borrower income, credit, and property approval. Not all borrowers will qualify. This is not an offer for extension of credit or a commitment to lend. Home valuations are estimates purchased from a third-party data provider. Actual appraised value at the time of a transaction may vary.</p><p>Fees and charges may apply and may vary by product and State. Loans may be secured by a lien against your property. Application may be required and subject to underwriting approval for any loan products. Not all loan applicants will be approved. Interest rates are based on national averages and are estimates for informational purposes only; they are not quoted rates (and any rates quoted by a professional are subject to change due to market conditions). If you apply for a loan and do not lock in a rate when you apply, your rate at closing may differ from the rate in effect when you applied. Important information relating specifically to any actual loan will be contained in the loan documents, which alone will establish your rights and obligations under the loan plan. Call for details. Terms, conditions and restrictions apply. The calculation on what you have paid toward principal and interest are estimated based on the available data (in some cases, entirely on public record data) and may not account for principal reductions, early or late payment penalties or other fees. To obtain exact and available options for your personal circumstances, please contact your lender.</p><p>With respect to any estimated home value data shown, Homebot purchases these data from a third-party provider and they are estimates based on an "automated valuation model" (a.k.a. an AVM, which is a computer algorithm) that analyzes recent property sales in the same area to estimate the value of a given property. The valuations are estimated based on available data and do not constitute an appraisal of the subject property and should not be relied upon in lieu of underwriting or an appraisal.  In some instances where an AVM is unavailable (esp. "new builds") or when other data are available that conflict with the AVM value, Homebot may choose to display the "appraised value," if provided by your loan officer, or "sold price," if available from your loan officer and/or public record in lieu of an AVM.  For approval of any actual loan products, a full appraisal will be required with your loan application to determine the actual value of your property, which may differ from the AVM estimate. The estimated cash-out available is calculated by subtracting your current loan balance from the AVM estimate of your property value and assumes closing costs are paid in cash. Any additional liens on your property would likely impact the amount of cash available.</p><p>Homebot provides the content in this webpage strictly for educational and informational purposes only. It is not intended to imply that a loan will be originated or approved by HUD, FHA, VA or any government-sponsored enterprise.</p><p>California Privacy Rights (a.k.a. "CCPA") are honored nationally.  If you are a California resident, you have the right to request information from us regarding the manner in which we share certain categories of Personal Information with third parties for their direct marketing purposes, in addition to the rights set forth above. Under California law, you have the right to send us a request at the designated address listed below to receive the following information:  1) the categories of information we disclosed to third parties for their direct marketing purposes during the preceding calendar year; 2) the names and addresses of the third parties that received the information; and 3) if the nature of the third party’s business cannot be determined from their name, examples of the products or services marketed.  This information may be provided in a standardized format that is not specific to you. The designated email address for these requests is: customersuccess@homebot.ai.  Note, even if you do not live in California, we will honor the same requests from you regardless of the State in which you live.  Also, please note that we have not yet developed a response to browser “Do Not Track” signals, and do not change any of our data collection practices when we receive such signals. We will continue to evaluate potential responses to “Do Not Track” signals in light of industry developments or legal changes.</p>'
                values={{ p: chunks => <p>{chunks}</p> }}
              />
              <FormattedMessage
                id='Disclaimer.bodyHTML.extra'
                defaultMessage='<p>You may be receiving Homebot from finance and/or real estate industry professionals, such as a loan officer or real estate agent. In some cases, you may be receiving Homebot from a real estate agent who is also providing you contact information for a loan officer (in a manner that is compliant with the CFPB and RESPA rules and regulations) in order to help provide answers related to financing. While it’s important to note that Homebot’s mission is to help you, the consumer, make the best and most informed choices when it comes to making smart home finance decisions, it is equally important to know you have choices and neither party is recommending or endorsing the other. You should choose the lender and/or real estate agent who best meets your needs.</p>'
                values={{ p: str => <p>{str}</p> }}
              />
            </>
          )}
        </Text>
        {displayFullDisclaimer && (
          <>
            <br />
            <CompanyDisclaimer />
          </>
        )}
        {!displayFullDisclaimer && (
          <p>
            <Button
              minimal
              onClick={() => setDisplayFullDisclaimer(true)}
              className={styles.disclaimerLink}
              data-qa='fullDisclaimerButton'
            >
              <FormattedMessage
                id='Footer.companyDisclaimer.showLink'
                defaultMessage='Read our full disclaimer'
              />
            </Button>
          </p>
        )}
      </div>
    </div>
  )
}

Disclaimer.propTypes = propTypes

export default Disclaimer
